import {
  ExternalLinkIcon,
  InformationCircleIcon,
  PlayIcon,
  PlusIcon,
} from "@heroicons/react/outline";
import {
  ArrowDown,
  ArrowUp,
  Banknote,
  BrainCircuit,
  ChevronRight,
  ClockIcon,
  DatabaseIcon,
  EllipsisVerticalIcon,
  ImageIcon,
  SlidersHorizontal,
  XIcon,
} from "lucide-react";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { v4 } from "uuid";

import LoadingSpinner from "./LoadingSpinner";
import { ModelParameters } from "./ModelParameters";
import { ModelNameButton } from "./ModelParameters/ModelNameButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown";
import { InputVariablesPopover } from "./ui/InputVariablesPopover";
import { BasicTooltip } from "./ui/Tooltip";

import { MetadataPopoverContent } from "@/components/MetadataPopoverContent";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { Button, DivButton } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { useEvaluateTagReports } from "@/hooks/evaluate/useEvaluateTagReports";
import { LLMProviderContext } from "@/providers/ProviderAndModelsProvider/provider-store";
import { useUpdateRequestTags } from "@/queries";
import { useModels } from "@/queries/models";
import type { SmartTableRequestImportPayload } from "@/queries/smart-tables";
import AddToTableDialog from "@/routes/analytics/RequestLogsAgGrid/AddToTableDialog";
import { PromptBlueprint } from "@/types/prompt-blueprint";
import { Request } from "@/types/requests";
import { convertKwargsToReadableFormat } from "@/utils/converKwargsToReadableFormat";
import { processTag, sortTagsWithLinksFirst } from "@/utils/tagUtils";
import { displayToast } from "@/utils/toast";
import { formatPrice, keepRelevantSearchParams } from "@/utils/utils";
import { REQUEST_LOG_SOURCE_PARAM } from "@/utils/requestLogUrlParams";

const REQUEST_PLAYGROUND_PARAM_KEYS = [REQUEST_LOG_SOURCE_PARAM];

// Calculate duration and return string using moment
function calculateDuration(start: string, end: string) {
  const duration = moment.duration(moment(end).diff(moment(start)));
  const seconds = duration.asSeconds();
  const milliseconds = duration.asMilliseconds();

  if (seconds < 1) {
    return `${milliseconds}ms`;
  } else if (seconds < 2 && milliseconds % 1000 > 0) {
    return `1s ${milliseconds % 1000}ms`;
  }
  return `${Math.round(seconds)}s`;
}

// Tag Component
const TagComponent = ({
  tag,
  idx,
  onDelete,
  editable = false,
  onEnter,
  workspaceId,
  evaluateMetadataMap,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tag: any;
  idx: number | string;
  onDelete?: () => void;
  editable?: boolean;
  onEnter?: (tag: string) => void;
  workspaceId?: string;
  evaluateMetadataMap?: Record<
    string,
    {
      is_blueprint: boolean;
      parent_report_id: number | null;
      workspace_id: number;
    }
  >;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleAddTag = () => {
    if (onEnter && inputValue.trim() !== "") {
      onEnter(inputValue);
      setIsEditing(false);
      setInputValue("");
    }
  };

  const editableTag = isEditing ? (
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      placeholder="Add tag"
      onChange={e => setInputValue(e.target.value)}
      className="border-none bg-transparent p-0 text-sm placeholder-gray-400 focus:outline-none focus:ring-0"
      onKeyDown={e => {
        if (e.key === "Enter") {
          handleAddTag();
        }
      }}
      style={{ width: `${Math.max(75, inputValue.length * 8)}px` }}
    />
  ) : (
    <div
      className="flex cursor-pointer items-center text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-500"
      onClick={() => {
        setIsEditing(true);
      }}
    >
      <span>Add tag</span> <PlusIcon className="ml-1 inline h-3 w-3" />
    </div>
  );

  // Process tag if it's a string, otherwise use as-is (e.g., LoadingSpinner)
  const tagProcessingResult =
    typeof tag === "string" ? processTag(tag, workspaceId, evaluateMetadataMap) : null;
  const tagLinkPath = tagProcessingResult?.linkPath ?? null;
  const isLink = tagProcessingResult?.isLink ?? false;
  const displayText = tagProcessingResult?.displayText ?? tag;
  const tagContent = editable ? editableTag : displayText;

  return (
    <>
      <div
        key={idx}
        className={`group items-center ${
          onDelete &&
          !isLink &&
          "transition-all duration-200 hover:border-red-100 hover:bg-red-100 hover:text-red-500"
        } ${
          editable && "border-gray-200 bg-gray-50"
        } relative inline rounded-full border border-gray-200 px-3 py-1 text-sm font-medium ${
          isLink
            ? "border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
            : "bg-gray-200 text-gray-500"
        }`}
      >
        {tagLinkPath ? (
          <BasicTooltip content={tag}>
            <Link
              to={tagLinkPath}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
              }}
              className="flex items-center gap-1.5 text-inherit no-underline hover:text-inherit"
            >
              <ExternalLinkIcon className="h-3 w-3 flex-shrink-0" />
              {tagContent}
            </Link>
          </BasicTooltip>
        ) : (
          tagContent
        )}
        {onDelete && !isLink ? (
          <div className="absolute -right-1 -top-1">
            <XIcon
              className="hidden h-4 w-4 cursor-pointer text-red-400 group-hover:block"
              onClick={onDelete}
            />
          </div>
        ) : null}
      </div>
      {editable && isEditing && (
        <Button size="sm" variant="default" onClick={handleAddTag}>
          <span>Add tag</span> <PlusIcon className="ml-1 inline h-3 w-3" />
        </Button>
      )}
    </>
  );
};

interface RequestAttribute {
  name: string;
  value: string;
  icon?: React.ReactNode;
  valueClassName?: string;
}

const RequestAttributeRow = ({ attribute }: { attribute: RequestAttribute }) => (
  <div className="flex items-center gap-x-1.5 whitespace-nowrap text-sm">
    {attribute.icon ? <span className="text-gray-400">{attribute.icon}</span> : null}
    <span className={attribute.valueClassName ?? "font-medium text-gray-800"}>
      {attribute.value}
    </span>
    <span className="text-xs text-gray-400">{attribute.name}</span>
  </div>
);

const RequestAttributesList = ({ attributes }: { attributes: RequestAttribute[] }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const measurementRef = useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(attributes.length);

  useEffect(() => {
    const wrapperElement = wrapperRef.current;
    const measurementElement = measurementRef.current;

    if (!wrapperElement || !measurementElement) {
      return;
    }

    let rafId = 0;

    const measure = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const itemElements = Array.from(
          measurementElement.querySelectorAll<HTMLElement>("[data-attribute-item]"),
        );
        const badgeElement =
          measurementElement.querySelector<HTMLElement>("[data-attribute-badge]");

        if (itemElements.length !== attributes.length || !badgeElement) {
          return;
        }

        const setMeasurementVisibility = (count: number, showBadge: boolean) => {
          itemElements.forEach((element, index) => {
            element.style.display = index < count ? "flex" : "none";
          });
          badgeElement.style.display = showBadge ? "inline-flex" : "none";
        };

        const getRenderedLineCount = () => {
          const visibleElements = itemElements.filter(element => element.style.display !== "none");
          const nodes =
            badgeElement.style.display === "none"
              ? visibleElements
              : [...visibleElements, badgeElement];
          return new Set(nodes.map(node => node.offsetTop)).size;
        };

        setMeasurementVisibility(attributes.length, false);
        if (getRenderedLineCount() <= 2) {
          setVisibleCount(previousCount =>
            previousCount !== attributes.length ? attributes.length : previousCount,
          );
          return;
        }

        let nextVisibleCount = 0;
        for (let count = attributes.length - 1; count >= 0; count -= 1) {
          setMeasurementVisibility(count, true);
          if (getRenderedLineCount() <= 2) {
            nextVisibleCount = count;
            break;
          }
        }

        setVisibleCount(previousCount =>
          previousCount !== nextVisibleCount ? nextVisibleCount : previousCount,
        );
      });
    };

    const observer = new ResizeObserver(measure);
    observer.observe(wrapperElement);
    measure();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [attributes]);

  const hiddenAttributes = attributes.slice(visibleCount);

  if (attributes.length === 0) {
    return null;
  }

  return (
    <div ref={wrapperRef} className="relative min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {attributes.slice(0, visibleCount).map((attribute, idx) => (
          <RequestAttributeRow
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            attribute={attribute}
          />
        ))}
        {hiddenAttributes.length > 0 ? (
          <BasicTooltip
            content={
              <div className="space-y-2">
                <div className="text-xs font-medium tracking-[0.08em] text-gray-500">
                  More Attributes
                </div>
                <div className="grid gap-2">
                  {hiddenAttributes.map((attribute, idx) => (
                    <RequestAttributeRow
                      // eslint-disable-next-line react/no-array-index-key
                      key={idx}
                      attribute={attribute}
                    />
                  ))}
                </div>
              </div>
            }
            variant="white"
            side="bottom"
            offset={6}
            contentClassName="w-[260px] p-3"
          >
            <Badge
              variant={BadgeVariant.GRAY}
              size={BadgeSize.SM}
              className="cursor-default"
              text={`+${hiddenAttributes.length}`}
            />
          </BasicTooltip>
        ) : null}
      </div>

      <div
        ref={measurementRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 -z-10 w-full"
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {attributes.map((attribute, idx) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              data-attribute-item
              className="flex"
            >
              <RequestAttributeRow attribute={attribute} />
            </div>
          ))}
          <Badge
            data-attribute-badge
            variant={BadgeVariant.GRAY}
            size={BadgeSize.SM}
            className="cursor-default"
            text="+0 Attributes"
          />
        </div>
      </div>
    </div>
  );
};

export const RequestInfoBlock = ({
  requestId,
  requestInfo,
  engine,
  displayName,
  provider,
  inputTokens,
  outputTokens,
  cacheReadInputTokens,
  cacheCreationInputTokens,
  disabled = false,
}: {
  requestId: string;
  requestInfo: Request;
  requestText: string;
  engine: string;
  displayName: string;
  provider: string;
  response_format?: string;
  inputTokens?: number;
  outputTokens?: number;
  cacheReadInputTokens?: number;
  cacheCreationInputTokens?: number;
  disabled?: boolean;
}) => {
  const [isAddToTableDialogOpen, setAddToTableDialogOpen] = useState(false);
  const { shareHash } = useParams();

  const authContext = useAuth();
  const userContext = useUser();

  const workspaceId = userContext?.activeWorkspaceId;
  const userToken = authContext?.userToken;
  const isLoggedIn = !!authContext?.userToken && !!userContext.user;
  const isSharedRequest = !!shareHash;
  const { data: models } = useModels({ providerId: requestInfo.provider_id });
  const providerContext = useContext(LLMProviderContext);
  const { providers } = providerContext?.store || {};

  const providerFromId = providers?.find(p => p.id === requestInfo.provider_id);

  const model = models?.model_configs?.find(m => m.id === requestInfo.model_config_id);

  const { mutate: updateTags, isLoading: updateTagsIsLoading } = useUpdateRequestTags(
    userToken || "",
  );

  // Fetch metadata for evaluate tags
  const { metadataMap: evaluateMetadataMap } = useEvaluateTagReports(requestInfo.tags_array || []);

  const buildRequestImportPayload = (): SmartTableRequestImportPayload | null => {
    const parsedRequestId = Number(requestId);
    if (!Number.isFinite(parsedRequestId)) {
      displayToast("Could not add request to table", ToastType.error);
      return null;
    }

    return {
      request_log_ids: [parsedRequestId],
    };
  };

  const handleDeleteTag = (tag: string) => {
    const newTags: string[] = requestInfo.tags_array?.filter(t => t !== tag) || [];
    updateTags({
      request_id: requestId,
      new_tags_list: newTags,
    });
  };

  const handleAddTag = (tag: string) => {
    const newTags: string[] = requestInfo.tags_array || [];
    newTags.push(tag);
    updateTags({
      request_id: requestId,
      new_tags_list: newTags,
    });
  };

  const parameters = model?.params || [];

  const paramKeys = [
    ...(parameters?.map(param => param.param_id) || []),
    ...(Object.keys(requestInfo.prompt_version?.metadata?.model?.parameters || {}) || []),
  ];

  const kwargs = requestInfo.prompt_version?.metadata?.model?.display_params || {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onlyTokens: Record<string, any> = Object.keys(kwargs || {})
    .filter(key => !paramKeys.includes(key))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .reduce((acc: Record<string, any>, key) => {
      if (kwargs) {
        acc[key] = kwargs[key];
      }
      return acc;
    }, {});
  const DISPLAY_PARAM_ICONS: Record<string, React.ReactNode> = {
    reasoning_tokens: <BrainCircuit className="h-4 w-4" />,
    image_tokens: <ImageIcon className="h-4 w-4" />,
    image_input_tokens: <ImageIcon className="h-4 w-4" />,
    cached_tokens: <DatabaseIcon className="h-4 w-4" />,
  };

  const modelAttributes = convertKwargsToReadableFormat(
    isSharedRequest ? kwargs : onlyTokens,
    parameters,
  );

  const mainAttributes = [
    {
      name: "Time",
      value: calculateDuration(requestInfo.request_start_time, requestInfo.request_end_time),
      icon: <ClockIcon className="h-4 w-4" />,
    },
    {
      name: "Cost",
      value: requestInfo.price ? formatPrice(requestInfo.price) : "-",
      icon: <Banknote className="h-4 w-4" />,
    },
    {
      name: "Input Tokens",
      value: inputTokens ? inputTokens.toString() : "-",
      icon: <ArrowUp className="h-4 w-4" />,
    },
    {
      name: "Output Tokens",
      value: outputTokens ? outputTokens.toString() : "-",
      icon: <ArrowDown className="h-4 w-4" />,
    },
    ...(cacheReadInputTokens
      ? [
          {
            name: "Cache Read",
            value: cacheReadInputTokens.toString(),
            icon: <DatabaseIcon className="h-4 w-4" />,
          },
        ]
      : []),
    ...(cacheCreationInputTokens
      ? [
          {
            name: "Cache Write",
            value: cacheCreationInputTokens.toString(),
            icon: <DatabaseIcon className="h-4 w-4" />,
          },
        ]
      : []),
  ].filter(attribute => attribute.value && attribute.value !== "");

  const allAttributes: RequestAttribute[] = [
    ...mainAttributes.map(attribute => ({
      ...attribute,
      valueClassName: "font-medium text-gray-800",
    })),
    ...modelAttributes.map(attribute => {
      const icon = DISPLAY_PARAM_ICONS[attribute.paramKey];

      return {
        name: attribute.name,
        value: attribute.value,
        icon,
        valueClassName: icon ? "font-medium text-gray-800" : "text-gray-800",
      };
    }),
  ];
  const [searchParams] = useSearchParams();

  const renderPlaygroundButton = () => {
    if (!isLoggedIn) {
      return null;
    }

    const prompt_blueprint = requestInfo.prompt_version;
    if (!prompt_blueprint) {
      return null;
    }

    if (disabled) {
      return (
        <Button size="sm" variant="secondaryLight" disabled>
          <PlayIcon className="mr-2 h-4 w-4" /> Open in Playground
        </Button>
      );
    }

    const requestIdentifier = shareHash || requestInfo.id;
    const basePlaygroundUrl = `/workspace/${workspaceId}/request/${requestIdentifier}/playground`;
    const buildPlaygroundUrl = (mode: "raw-request" | "prompt-version") => {
      const params = new URLSearchParams(
        keepRelevantSearchParams(searchParams, REQUEST_PLAYGROUND_PARAM_KEYS),
      );
      params.set("mode", mode);
      return `${basePlaygroundUrl}/${v4()}?${params.toString()}`;
    };

    if (!requestInfo.prompt_id) {
      return (
        <Link to={buildPlaygroundUrl("raw-request")}>
          <Button size="sm" variant="secondaryLight">
            <PlayIcon className="mr-2 h-4 w-4" /> Open in Playground
          </Button>
        </Link>
      );
    }

    return (
      <div className="flex items-center divide-x divide-blue-300">
        <Link to={buildPlaygroundUrl("prompt-version")}>
          <Button size="sm" variant="secondaryLight" className="rounded-r-none">
            <PlayIcon className="mr-2 h-4 w-4" /> Open in Playground
          </Button>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger className="group">
            <Button size="sm" variant="secondaryLight" className="rounded-l-none">
              <ChevronRight className="h-4 w-4 transition group-data-[state=open]:rotate-90" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem asChild>
              <Link to={buildPlaygroundUrl("prompt-version")} className="w-full">
                Prompt Template
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={buildPlaygroundUrl("raw-request")} className="w-full">
                Raw Request
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const hasVariables =
    !!requestInfo.prompt_input_variables &&
    Object.keys(requestInfo.prompt_input_variables).length > 0;

  return (
    <>
      <div className="relative w-full rounded-md border border-gray-200 bg-white px-4 py-3 text-gray-700">
        {isLoggedIn && !isSharedRequest && (
          <div className="absolute right-4 top-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                  disabled={disabled}
                >
                  <EllipsisVerticalIcon className="h-4 w-4" />
                  <span className="sr-only">More actions</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-52 p-1">
                {requestInfo.playground_session_id && (
                  <Link
                    to={`/workspace/${workspaceId}/playground/${requestInfo.playground_session_id}`}
                    className="flex w-full items-center rounded px-3 py-2 text-sm text-inherit no-underline hover:bg-gray-50"
                  >
                    <ExternalLinkIcon className="mr-2 h-4 w-4 text-gray-500" />
                    Open Original Session
                  </Link>
                )}
                <ModelParameters
                  promptData={requestInfo.prompt_version as Partial<PromptBlueprint>}
                  buttonSize="sm"
                  triggerLabel="Model Parameters"
                  triggerClassName="h-auto w-full justify-start rounded px-3 py-2 text-sm font-normal hover:!bg-gray-50 hover:!text-inherit"
                  triggerLabelClassName="max-w-none text-sm font-normal"
                  triggerLogo={<SlidersHorizontal className="mr-2 h-4 w-4 text-gray-500" />}
                  isPromptChat={!!requestInfo.is_chat}
                  showTooltip={false}
                  readonly
                  triggerVariant="ghost"
                  showLearnMore={false}
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center rounded px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      <InformationCircleIcon className="mr-2 h-4 w-4 text-gray-500" />
                      Metadata
                    </button>
                  </PopoverTrigger>
                  <MetadataPopoverContent requestId={requestId} />
                </Popover>
              </PopoverContent>
            </Popover>
          </div>
        )}
        <div className="flex flex-col gap-2">
          {/* Row 1: Model + metrics (flat flex so individual items wrap) */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pr-10">
            <ModelNameButton
              provider={
                providerFromId?.name ||
                requestInfo.prompt_version?.metadata?.model?.provider ||
                provider
              }
              modelName={displayName || engine}
              providerDisplay={providerFromId?.name || provider}
              asButton={false}
              readOnly
              showTooltip={false}
              functionName={requestInfo.function_name}
            />
            <div className="h-5 w-px bg-gray-200" />
            <RequestAttributesList attributes={allAttributes} />
          </div>
          {/* Row 2: Actions (left) + Tags (right) */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2 border-t border-gray-100 pt-2">
            {renderPlaygroundButton()}
            {isLoggedIn && !isSharedRequest && (
              <DivButton
                onClick={() => setAddToTableDialogOpen(true)}
                size="sm"
                variant="secondaryLightOutline"
                disabled={disabled}
              >
                <PlusIcon className="mr-2 h-4 w-4" /> Add to Table
              </DivButton>
            )}
            {hasVariables && (
              <InputVariablesPopover
                variables={requestInfo.prompt_input_variables}
                requestId={requestId}
                disabled={disabled}
              />
            )}
            <div className="ml-auto flex flex-wrap items-center gap-x-2 gap-y-1">
              {sortTagsWithLinksFirst(requestInfo.tags_array || []).map(
                (tag: string, idx: number) => (
                  <TagComponent
                    key={tag}
                    tag={tag}
                    idx={idx}
                    onDelete={() => handleDeleteTag(tag)}
                    workspaceId={workspaceId?.toString() || undefined}
                    evaluateMetadataMap={evaluateMetadataMap}
                  />
                ),
              )}
              {updateTagsIsLoading ? (
                <TagComponent tag={<LoadingSpinner size={4} />} idx="loading" />
              ) : (
                <TagComponent tag="Add Tag" idx="add" editable onEnter={handleAddTag} />
              )}
            </div>
          </div>
        </div>
      </div>
      <AddToTableDialog
        isOpen={isAddToTableDialogOpen}
        setOpen={setAddToTableDialogOpen}
        buildPayload={buildRequestImportPayload}
      />
    </>
  );
};
